import { NavLink, useLocation, type NavLinkProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import { palette } from '../../styles/constants';

type TabProps = {
    children?: React.ReactNode;
    path: string;
} & Omit<NavLinkProps, 'to'>;

type UnpackedUrlParams = {
    [key: string]: string[];
}

const getQueryParamsDict = (queryString: string): UnpackedUrlParams => {
    const queryParams = new URLSearchParams(queryString);
    const entries = [...queryParams.entries()];
    return entries.reduce((acc: UnpackedUrlParams, [key, value]) => {
        if (key in acc) {
            acc[key] = [...acc[key], value].sort();
        } else {
            acc[key] = [value];
        }
        return acc;
    }, {});
};

const paramsAreEqual = (a: UnpackedUrlParams, b: UnpackedUrlParams) => {
    return JSON.stringify(a) === JSON.stringify(b);
};

const splitPathForComparison = (url: string) => {
    const split = url.split('?');
    const path = split[0];
    const query = split[1] || '';
    return [path, query];
};

export default function Tab({ children, path, ...navLinkProps }: TabProps) {
    const location = useLocation();
    const currentQueryParamsDict = getQueryParamsDict(location.search.substring(1));
    const [mainPath, mainQuery] = splitPathForComparison(path);
    const pathnameWithHash = `${location.pathname}${location.hash}`;
    const isActive = pathnameWithHash === mainPath && paramsAreEqual(currentQueryParamsDict, getQueryParamsDict(mainQuery));
    return <Wrapper {...navLinkProps} exact activeClassName="active" to={path} isActive={() => isActive}>
        {children}
    </Wrapper>;
}

const Wrapper = styled(NavLink)`
  display: flex;
  flex-direction: row;
  padding: 8px 16px 16px;
  align-items: flex-start;
  color: ${palette.neutral[600]};
  gap: 8px;
  align-self: stretch;
  border-bottom: 3px solid transparent;
  transition: all 0.1s ease-in-out;
  text-decoration: none;

    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 20px; /* 142.857% */

  &:hover {
    color: ${palette.neutral[900]};
  }

  &.active {
    border-color: ${palette.primary[500]};
    color: ${palette.neutral[900]};
  }
`;
